import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Inject,
  ViewChild,
  TemplateRef,
  ChangeDetectorRef,
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable, tap } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import cloneDeep from 'lodash.clonedeep';

import { CopyDprOptionGridData } from '@dpr-details/models/grid';
import { StaticMessage } from '@shared/enums';
import { GridApi, GridColumn, GridOptions } from '@grid/models';
import { GridComponent } from '@grid/grid.component';
import { TextDisplayComponent } from '@grid/components';
import {
  CustomCheckboxEditComponent,
  NotApplicableDisplayComponent,
} from '@shared/components';
import { GridColumnTemplates } from '@shared/utils/grid';

@UntilDestroy()
@Component({
  selector: 'app-copy-from-dpr-modal',
  templateUrl: './copy-from-dpr-modal.component.html',
  styleUrls: ['./copy-from-dpr-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CopyFromDprModalComponent implements OnInit {
  //#region Templates

  @ViewChild(GridComponent)
  gridComponent!: GridComponent<CopyDprOptionGridData>;

  @ViewChild('copyColumnHeaderTemplate', { static: true })
  copyColumnHeaderTemplate: TemplateRef<any>;

  //#endregion

  gridColumns: GridColumn<CopyDprOptionGridData>[] = [];
  gridOptions!: GridOptions<CopyDprOptionGridData>;
  gridData: CopyDprOptionGridData[];
  staticMessage = StaticMessage;

  constructor(
    public dialogRef: MatDialogRef<CopyFromDprModalComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      title: string;
      gridData: CopyDprOptionGridData[];
      isAppOnline$: Observable<boolean>;
    },
    private cdr: ChangeDetectorRef
  ) {
    this.gridData = cloneDeep(this.data.gridData);
  }

  ngOnInit(): void {
    this.gridOptions = {
      addConfig: {
        allowAdding: false,
      },
      deleteConfig: {
        allowDeleting: false,
        isExternalDelete: true,
      },
      editConfig: {
        allowEditing: true,
      },
      saveConfig: {
        isExternalSave: true,
      },
      rowConfig: {
        allowDeleting: false,
        allowSelection: this.canFeatureBeCopied.bind(this),
      },
      changesConfig: {
        disableHighlighting: true,
      },
      selectionConfig: {
        allowSelection: true,
      },
      sequenceConfig: {
        disabled: true,
      },
      headerConfig: {
        displayHeader: false,
      },
      events: {
        onGridRegistered: this.initSelection.bind(this),
      },
    };

    this.gridColumns = [
      {
        columnDef: 'section',
        header: {
          name: $localize`:@@dprDetails.copyFromDprModal.colSection:Section`,
        },
        allowEditing: false,
        allowSorting: false,
        display: {
          type: TextDisplayComponent,
        },
        tabIndex: -1,
        disableFocusedBackground: true,
        width: 180,
      },
      {
        ...GridColumnTemplates.checkbox,
        columnDef: 'includeAttachments',
        header: {
          ...GridColumnTemplates.checkbox.header,
          name: $localize`:@@dprDetails.copyFromDprModal.colIncludeAttachments:Include attachments`,
        },
        display: (data: CopyDprOptionGridData) => ({
          type: data.allowIncludeAttachments
            ? CustomCheckboxEditComponent
            : NotApplicableDisplayComponent,
        }),
        tooltip: {
          disabled: true,
        },
      },
      {
        ...GridColumnTemplates.checkbox,
        columnDef: 'includeCustomData',
        header: {
          ...GridColumnTemplates.checkbox.header,
          name: $localize`:@@dprDetails.copyFromDprModal.colIncludeCustomData:Include custom data`,
        },
        display: (data: CopyDprOptionGridData) => ({
          type: data.allowIncludeCustomData
            ? CustomCheckboxEditComponent
            : NotApplicableDisplayComponent,
        }),
        tooltip: {
          disabled: true,
        },
      },
    ];
  }

  initSelection(gridApi: GridApi<CopyDprOptionGridData>): void {
    gridApi.selectAll();

    gridApi.selected$
      .pipe(
        untilDestroyed(this),
        tap((selected) => {
          const toBeUnselected = this.gridData.filter(
            (x) => !selected.includes(x)
          );
          toBeUnselected.forEach((data) => {
            this.gridComponent.api.editCell(data, 'copy', false);
            this.gridComponent.api.editCell(data, 'includeAttachments', false);
            this.gridComponent.api.editCell(data, 'includeCustomData', false);
          });

          selected.forEach((data) => {
            this.gridComponent.api.editCell(data, 'copy', true);
          });
        })
      )
      .subscribe();

    this.cdr.detectChanges();
  }

  onCopy(): void {
    this.dialogRef.close({
      ...this.gridComponent.api.getChanges(),
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  //#region Private methods

  private canFeatureBeCopied(data: CopyDprOptionGridData): boolean {
    return !data?.disable;
  }

  //#endregion
}
